import React from 'react'
import { hasTreatmentName } from '../utils/treatment-name'
import { isFeatureEnabled, isStateFeatureEnabled } from '../utils/remote-config'
import { Message } from '@paypalcorp/worldready-react'
import { SHOPPING_PRIVACY_FLOW_REDESIGN } from '../constants/constants'
import { dataPrivacyConfig } from './data-privacy-data-platform'
import { pageSpec } from './fptiConstants'

function isEnabledForPermission(serverData) {
  return (
    !isFeatureEnabled('enabledForRecommendations') &&
    !serverData.isMerchantUser &&
    isFeatureEnabled('enabledForPermission')
  )
}

function isEnabledForSearchPrivacy(serverData) {
  return (
    isFeatureEnabled('enabledForSearchPrivacy') &&
    (serverData.isPublicProfile ||
      isFeatureEnabled('enabledForSimplifiedSearchPrivacy'))
  )
}

function isEnabledForMarketing(serverData) {
  return (
    !serverData.isMerchantUser &&
    (isFeatureEnabled('enabledForMarketing') ||
      (hasTreatmentName('Trmt_Interest_Based_Marketing_DataPrivacy_Ramp') &&
        isStateFeatureEnabled('excludedStatesForMarketing')))
  )
}

function isEnabledForShopping(serverData) {
  return !serverData.isMerchantUser && isFeatureEnabled('enabledForShopping')
}

function isEnabledForBlockedContacts() {
  return isFeatureEnabled('enabledForBlockedContacts')
}

function getFeatureFlags(serverData) {
  const enabledForNative = serverData.isNative
  const enabledForManageCookies = !enabledForNative
  const enabledForPermission = isEnabledForPermission(serverData)
  const enabledForSearchPrivacy = isEnabledForSearchPrivacy(serverData)
  const enabledForMarketing = isEnabledForMarketing(serverData)
  const enabledForShopping = isEnabledForShopping(serverData)
  const enabledForBlockedContacts = isEnabledForBlockedContacts()

  const enabledForManagePrivacySection =
    enabledForPermission ||
    enabledForManageCookies ||
    enabledForMarketing ||
    enabledForShopping ||
    enabledForSearchPrivacy ||
    enabledForBlockedContacts

  return {
    enabledForNative,
    isMerchantUser: serverData.isMerchantUser || false,
    isPublicProfile: serverData.isPublicProfile,
    enabledForPermission,
    enabledForManageCookies,
    enabledForSearchPrivacy,
    enabledForMarketing,
    enabledForShopping,
    enabledForBlockedContacts,
    enabledForManagePrivacySection,
    isInFrame: serverData.isInFrame,
  }
}

const linkClassName = 'nemo-subSection-header subSection-header'

function privacySections(serverData) {
  const featureFlags = getFeatureFlags(serverData)
  return [
    {
      sectionId: 'privacySection',
      sectionName: <Message id="privacy.sectionName" />,
      isEnabled: featureFlags.enabledForManagePrivacySection,
      enabledCountries: [],
      hasElmoTreatment: true,
      subSections: [
        {
          subSectionId: 'permissions',
          title: <Message id="privacy.permissions.title" />,
          desc: <Message id="privacy.permissions.desc" />,
          icon: dataPrivacyConfig.permission.icon,
          linkClass: linkClassName,
          hasElmoTreatment: true,
          featureEnabled: featureFlags.enabledForPermission, //rcs service
          navigateTo:
            '/myaccount/security/flow/permissions/thirdparty?flow=cmV0dXJuVXJsPS9teWFjY291bnQvcHJpdmFjeSZjYW5jZWxVcmw9L215YWNjb3VudC9wcml2YWN5',
          fptiClickData: pageSpec.privacy_sections_permissions_given_Link(),
        },
        {
          subSectionId: 'manageCookies',
          title: (
            <Message
              id={
                featureFlags.isMerchantUser
                  ? 'privacy.merchant.cookies.title'
                  : 'privacy.manageCookies.title'
              }
            />
          ),
          desc: <Message id="privacy.manageCookies.desc" />,
          icon: !featureFlags.isMerchantUser
            ? dataPrivacyConfig.manageCookie.icon
            : '',
          linkClass: linkClassName,
          hasElmoTreatment: true,
          featureEnabled: featureFlags.enabledForManageCookies, //rcs service
          navigateTo:
            featureFlags.isInFrame && featureFlags.isMerchantUser
              ? `/myaccount/privacy/cookiePrefs?inframe=${featureFlags.isInFrame}`
              : '/myaccount/privacy/cookiePrefs',
          fptiClickData: pageSpec.privacy_section_manage_cookie_link(
            featureFlags.isMerchantUser,
          ),
          action: <Message id="privacy.merchant.action.manage" />,
        },
        {
          subSectionId: 'interestMarketing',
          title: <Message id="privacy.marketing.title" />,
          desc: <Message id="privacy.marketing.desc" />,
          icon: dataPrivacyConfig.marketing.icon,
          linkClass: linkClassName,
          hasElmoTreatment: true,
          featureEnabled: featureFlags.enabledForMarketing,
          modal: {
            name: 'interestMarketingModal',
          },
          fptiClickData: pageSpec.privacy_section_interest_marketing_link(),
        },
        {
          subSectionId: 'ppShopping',
          title: <Message id="privacy.shopping.title" />,
          desc: <Message id="privacy.shopping.new.desc" />,
          icon: dataPrivacyConfig.shopping.icon,
          linkClass: linkClassName,
          hasElmoTreatment: true,
          featureEnabled: featureFlags.enabledForShopping,
          navigateTo: SHOPPING_PRIVACY_FLOW_REDESIGN,
          fptiClickData: pageSpec.privacy_section_pp_shopping_link(),
        },
        {
          subSectionId: 'searchPrivacy',
          title: <Message id="privacy.options.title" />,
          desc: isFeatureEnabled('searchPrivacyAlternateDesc') ? (
            <Message id="privacy.searchPrivacy.descAlternate" />
          ) : (
            <Message id="privacy.searchPrivacy.desc" />
          ),
          icon: !featureFlags.isMerchantUser
            ? dataPrivacyConfig.searchPrivacy.icon
            : '',
          linkClass: linkClassName,
          hasElmoTreatment: true,
          featureEnabled: featureFlags.enabledForSearchPrivacy,
          navigateTo:
            featureFlags.isInFrame && featureFlags.isMerchantUser
              ? `/myaccount/privacy/profiles/search?inframe=${featureFlags.isInFrame}`
              : '/myaccount/privacy/profiles/search',
          fptiClickData: pageSpec.privacy_section_search_privacy_link(
            featureFlags.isMerchantUser,
          ),
          action: <Message id="privacy.merchant.action.update" />,
        },
        {
          subSectionId: 'blockedContacts',
          title: <Message id="privacy.blockedContacts.title" />,
          desc: <Message id="privacy.blockedContacts.desc" />,
          icon: !featureFlags.isMerchantUser
            ? dataPrivacyConfig.blockedContacts.icon
            : '',
          linkClass: linkClassName,
          hasElmoTreatment: true,
          featureEnabled: featureFlags.enabledForBlockedContacts,
          navigateTo:
            featureFlags.isInFrame && featureFlags.isMerchantUser
              ? `/myaccount/transfer/homepage/contact-list/blocked-contacts?inframe=${featureFlags.isInFrame}`
              : '/myaccount/transfer/homepage/contact-list/blocked-contacts',
          fptiClickData: pageSpec.privacy_section_blocked_contacts_link(
            featureFlags.isMerchantUser,
          ),
          action: <Message id="privacy.merchant.action.update" />,
        },
      ],
    },
  ]
}

export default privacySections
